import { useHandler } from "@redotech/react-util/hook";
import { ForwardedRef, forwardRef, memo, useEffect, useState } from "react";
import { Flex } from "../../flex";
import { BaseRedoInputContainer } from "./base-redo-input-container";
import {
  BaseRedoInput,
  RedoInputSize,
  RedoInputState,
} from "./base-redo-text-input";
import { BaseRedoInputInfoTooltip } from "./base-redo-text-input-info-tooltip";

export interface RedoDateInputProps {
  value: Temporal.PlainDate | null;
  setValue(value: Temporal.PlainDate | null): void;
  size?: RedoInputSize;
  state?: RedoInputState;
  label?: string;
  description?: string;
  errorText?: string;
  infoTooltip?: string;
  required?: boolean;
  className?: string;
  fullWidth?: boolean;
  autoFocus?: boolean;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  name?: string;
  "data-testid"?: string;
}

export const RedoDateInput = memo(
  forwardRef(function RedoDateInput(
    {
      size = "sm",
      value,
      setValue,
      errorText,
      infoTooltip,
      label,
      description,
      state = "default",
      className,
      required,
      fullWidth,
      autoFocus,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      name,
      "data-testid": dataTestId,
    }: RedoDateInputProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const [inputText, setInputText] = useState<string>(formatDate(value));

    useEffect(() => {
      setInputText(formatDate(value));
    }, [value]);

    const parseInputDate = useHandler((input: string) => {
      const result = parseDateInputText(input, inputText);
      if (result.value !== undefined) {
        setValue(result.value);
      }
      setInputText(result.text);
    });

    return (
      <BaseRedoInputContainer
        className={className}
        dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
        }
        data-arbiter="RedoDateInput"
        description={description}
        errorText={errorText}
        fullWidth={fullWidth}
        label={label}
        size={size}
        state={state}
      >
        <Flex align="center" dir="row" grow={1} maxW="full">
          <BaseRedoInput
            autoFocus={autoFocus}
            dataTestId={dataTestId}
            name={name}
            placeholder="MM-DD-YYYY"
            ref={ref}
            required={required}
            setValue={parseInputDate}
            size={size}
            state={state}
            value={inputText}
          />
          {infoTooltip && (
            <BaseRedoInputInfoTooltip
              infoTooltip={infoTooltip}
              size={size}
              state={state}
            />
          )}
        </Flex>
      </BaseRedoInputContainer>
    );
  }),
);

function formatDate(date: Temporal.PlainDate | null): string {
  if (!date) {
    return "";
  }

  const year = date.toLocaleString("en-US", { year: "numeric" });
  const month = date.toLocaleString("en-US", { month: "2-digit" });
  const day = date.toLocaleString("en-US", { day: "2-digit" });
  return `${month}-${day}-${year}`;
}

const FULL_DATE_REGEX = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
const MONTH_ONLY_REGEX = /^(\d{1,2})$/;
const MONTH_DAY_REGEX = /^(\d{1,2})-(\d{1,2})$/;
const MONTH_LENGTH = 2;
const DAY_LENGTH = 2;
const YEAR_LENGTH = 4;
const MONTHS_IN_YEAR = 12;
// Days 1-3 are ambiguous while typing (could be the first digit of 10-31), so
// a single-digit day only auto-advances past this threshold.
const MAX_AMBIGUOUS_DAY = 3;
// Any leap year works for resolving the day count of a bare MM-DD.
const LEAP_YEAR = 2000;

export interface DateInputParseResult {
  text: string;
  /** undefined means "leave the current value untouched" */
  value?: Temporal.PlainDate | null;
}

export function parseDateInputText(
  rawInput: string,
  previousText: string,
): DateInputParseResult {
  if (!rawInput.trim()) {
    return { text: rawInput, value: null };
  }

  // A complete date is terminal: appending would only corrupt the year (2000 ->
  // 20009), so ignore the keystroke. Deleting/replacing still edits normally.
  const appending = rawInput.length > previousText.length;
  if (appending && FULL_DATE_REGEX.test(previousText)) {
    return { text: previousText };
  }

  const input = addDashesToDigitsOnlyInput(rawInput);

  let value: Temporal.PlainDate | undefined;
  const fullMatch = input.match(FULL_DATE_REGEX);
  if (fullMatch) {
    try {
      value = Temporal.PlainDate.from(
        `${fullMatch[3]}-${fullMatch[1]}-${fullMatch[2]}`,
      );
    } catch {
      value = undefined;
    }
  }

  const deleting = rawInput.length < previousText.length;
  if (deleting) {
    return { text: rawInput, value };
  }

  const monthMatch = input.match(MONTH_ONLY_REGEX);
  if (monthMatch) {
    const monthText = monthMatch[1] ?? "";
    const month = clamp(Number(monthText), 1, MONTHS_IN_YEAR);
    if (month > 1 || monthText.length === MONTH_LENGTH) {
      return { text: `${pad2(month)}-`, value };
    }
  }

  const monthDayMatch = input.match(MONTH_DAY_REGEX);
  if (monthDayMatch) {
    const month = clamp(Number(monthDayMatch[1]), 1, MONTHS_IN_YEAR);
    const daysInMonth = Temporal.PlainYearMonth.from({
      month,
      year: LEAP_YEAR,
    }).daysInMonth;

    const dayText = monthDayMatch[2] ?? "";
    const day = clamp(Number(dayText), 1, daysInMonth);
    if (day > MAX_AMBIGUOUS_DAY || dayText.length === DAY_LENGTH) {
      return { text: `${pad2(month)}-${pad2(day)}-`, value };
    }
  }

  return { text: input, value };
}

// Recovers input typed or pasted without separators (e.g. "01011990") by
// slotting the digits into MM-DD-YYYY.
function addDashesToDigitsOnlyInput(input: string): string {
  if (!/^\d+$/.test(input) || input.length <= MONTH_LENGTH) {
    return input;
  }
  const dayEnd = MONTH_LENGTH + DAY_LENGTH;
  const parts = [
    input.slice(0, MONTH_LENGTH),
    input.slice(MONTH_LENGTH, dayEnd),
    input.slice(dayEnd, dayEnd + YEAR_LENGTH),
  ];
  return parts.filter((part) => part.length > 0).join("-");
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pad2(value: number): string {
  return value.toString().padStart(MONTH_LENGTH, "0");
}
